import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  Avatar
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  GroupAdd as GroupAddIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import axios from 'axios';

interface Group {
  id: string;
  name: string;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

interface GroupsProps {
  onGroupSelect?: (groupId: string, groupName: string) => void;
}

const Groups: React.FC<GroupsProps> = ({ onGroupSelect }) => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [groupName, setGroupName] = useState('');
  const [nameError, setNameError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadGroups();
  }, []);

  useEffect(() => {
    filterGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, groups]);

  const loadGroups = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/groups');
      setGroups(response.data.groups || []);
    } catch (err: any) {
      console.error('Error loading groups:', err);
      setError(err.response?.data?.error || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const filterGroups = () => {
    if (!searchQuery.trim()) {
      setFilteredGroups(groups);
      return;
    }
    const query = searchQuery.toLowerCase();
    const filtered = groups.filter(group => group.name.toLowerCase().includes(query));
    setFilteredGroups(filtered);
  };

  const handleOpenDialog = (group?: Group) => {
    if (group) {
      setEditingGroup(group);
      setGroupName(group.name);
    } else {
      setEditingGroup(null);
      setGroupName('');
    }
    setNameError('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingGroup(null);
    setGroupName('');
    setNameError('');
  };

  const validateForm = (): boolean => {
    if (!groupName.trim()) {
      setNameError('Group name is required');
      return false;
    }
    setNameError('');
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      setError('');
      if (editingGroup) {
        await axios.put(`/api/groups/${editingGroup.id}`, { name: groupName.trim() });
        setSuccess('Group updated successfully');
      } else {
        await axios.post('/api/groups', { name: groupName.trim() });
        setSuccess('Group created successfully');
      }
      handleCloseDialog();
      loadGroups();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error saving group:', err);
      const errorMsg = err.response?.data?.error || 'Failed to save group';
      if (errorMsg.includes('already exists') || errorMsg.includes('duplicate')) {
        setNameError('A group with this name already exists');
      } else {
        setError(errorMsg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (group: Group) => {
    if (!window.confirm(`Are you sure you want to delete "${group.name}"? All contacts will be preserved.`)) return;
    try {
      setError('');
      await axios.delete(`/api/groups/${group.id}`);
      setSuccess('Group deleted successfully');
      loadGroups();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error deleting group:', err);
      setError(err.response?.data?.error || 'Failed to delete group');
    }
  };

  const handleGroupClick = (group: Group) => {
    if (onGroupSelect) {
      onGroupSelect(group.id, group.name);
    }
  };

  const getGroupIcon = (name: string) => {
    const icons = ['👥', '🚀', '🎯', '🔔', '💼', '📊', '🌍', '⚡'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return icons[Math.abs(hash) % icons.length];
  };

  const getAvatarColor = (name: string) => {
    const colors = ['#6c5ce7', '#00b894', '#0984e3', '#e17055', '#fdcb6e', '#e84393', '#00cec9'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  };

  const formatMemberCount = (count: number) => {
    if (count >= 1000) return `${(count / 1000).toFixed(count >= 10000 ? 0 : 1).replace(/\.0$/, ',')}${count.toLocaleString()} Members`;
    return `${count} Member${count !== 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header row with title, search, and create button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2d3436', fontSize: { xs: '1.25rem', md: '2.125rem' } }}>Groups</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search groups..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: { xs: '100%', sm: 200 }, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>,
            }}
          />
          <Button
            variant="contained"
            startIcon={<GroupAddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              bgcolor: '#6c5ce7',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: { xs: 2, md: 3 },
              fontSize: { xs: '0.8rem', md: '0.875rem' },
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: '#5a4bd1' }
            }}
          >
            Create Group
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', maxWidth: '100%' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }}>Group Name</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }}>Members</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }}>Created Date</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredGroups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      {searchQuery ? 'No groups found' : 'No groups yet. Click "Create Group" to get started.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredGroups.map((group) => (
                  <TableRow
                    key={group.id}
                    onClick={() => handleGroupClick(group)}
                    sx={{ cursor: onGroupSelect ? 'pointer' : 'default', '&:hover': { bgcolor: '#f9f9fc' } }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            bgcolor: `${getAvatarColor(group.name)}18`,
                            width: 38,
                            height: 38,
                            fontSize: 18,
                          }}
                        >
                          {getGroupIcon(group.name)}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2d3436' }}>
                          {group.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatMemberCount(group.memberCount)}
                        size="small"
                        sx={{
                          bgcolor: '#f0edff',
                          color: '#6c5ce7',
                          fontWeight: 500,
                          fontSize: 12,
                          height: 26,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#555' }}>
                        {formatDate(group.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleGroupClick(group); }}
                        title="Manage Members"
                        sx={{ color: '#999' }}
                      >
                        <PersonAddIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleOpenDialog(group); }}
                        title="Edit"
                        sx={{ color: '#999' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleDelete(group); }}
                        title="Delete"
                        sx={{ color: '#999', '&:hover': { color: '#e74c3c' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGroup ? 'Edit Group' : 'Create Group'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              label="Group Name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              error={!!nameError}
              helperText={nameError}
              required
              fullWidth
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={{ bgcolor: '#6c5ce7', '&:hover': { bgcolor: '#5a4bd1' } }}
          >
            {saving ? <CircularProgress size={24} /> : (editingGroup ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Groups;
